import { FAMILY_LABEL } from '../data/families.js';
import { PLANT_TYPE } from '../data/plants.js';
import {
  formatMoney,
  formatEmissions,
  formatPlantOutput,
  formatGw,
  roundGw,
  formatTwh,
  formatPercent
} from './format.js';
import { storageSurplusToFill } from '../engine/advancedCapacity.js';

function buildOpexReasonLines(dynamicOpex) {
  const lines = [];
  lines.push(`Base opex: ${formatMoney(dynamicOpex.base)}`);
  if (dynamicOpex.eosFromDuplicate) {
    // Read the multiplier rather than hardcoding it: Advanced Mode
    // uses a weaker duplicate discount than Classic (section 16.14).
    lines.push(`Duplicate EOS: x${dynamicOpex.eosMultiplier}`);
  }
  if (dynamicOpex.subsidyApplied) {
    lines.push(`Fossil fuel subsidy: x0.5`);
  }
  if (dynamicOpex.floored) {
    lines.push(`Floored at ${formatMoney(0.5)} minimum`);
  }
  lines.push(`Effective opex: ${formatMoney(dynamicOpex.final)}`);
  return lines;
}

function buildCapitalReasonLines(plant, resourcePremium, effectiveCapital) {
  return [
    `Listed capital: ${formatMoney(plant.capital)}`,
    `Resource premium: +${formatMoney(resourcePremium)}`,
    `Total capital: ${formatMoney(effectiveCapital)}`
  ];
}

function baseFirmValue(plant) {
  return (plant.capacityUnits ?? 0) * (plant.firmCapacityCredit ?? 0);
}

function baseFirmLabel(plant) {
  return formatGw(baseFirmValue(plant));
}

// Firm capacity moves with the plan the same way opex does, and for a
// reason the card cannot otherwise show: the variable diversity bonus
// is added to a plant's credit, so a plant that would open a new
// variable family is worth more than its bare rating — and it also
// lifts every variable plant already planned, which is the larger half
// of the gain and belongs in the tooltip rather than the figure.
function buildFirmReasonLines(dynamicFirm) {
  const base = roundGw(dynamicFirm.base);
  const effective = roundGw(dynamicFirm.effective);
  const diversityAdjusted = roundGw(
    dynamicFirm.base + dynamicFirm.diversityBonus
  );
  const diversityBonus = roundGw(diversityAdjusted - base);
  const lines = [`Base firm capacity: ${formatGw(base)}`];
  if (diversityBonus > 0) {
    lines.push(`Variable mix bonus: +${formatGw(diversityBonus)}`);
  }
  lines.push(
    `Family copy credit: ${formatPercent(dynamicFirm.familyCreditFactor)}`
  );
  if (dynamicFirm.addsFamily && dynamicFirm.fleetGain > 0) {
    lines.push(
      `Opens variable family ${dynamicFirm.familiesAfter}, lifting your other variable plants by +${formatGw(dynamicFirm.fleetGain)}`
    );
  } else if (dynamicFirm.fleetGain < -1e-9) {
    lines.push(
      `Family saturation reduces your other variable plants by ${formatGw(Math.abs(dynamicFirm.fleetGain))}`
    );
  }
  lines.push(`Effective firm capacity: ${formatGw(effective)}`);
  return lines;
}

export function PlantTile({
  plant,
  onSelect,
  onRemove,
  selectable = false,
  removable = false,
  disabled = false,
  constructing = false,
  constructionDurationMs,
  rolledOutput,
  dynamicOpex,
  dynamicCapital,
  dynamicFirm,
  outputMultiplier = 1,
  showFirmCapacity = false,
  testId
}) {
  const isVariable = plant.type === PLANT_TYPE.VARIABLE;
  const isStorage = plant.type === 'storage';
  const outputLabel = isStorage
    ? 'No direct output'
    : formatPlantOutput(
        plant,
        rolledOutput !== undefined ? { [plant.id]: rolledOutput } : null,
        outputMultiplier
      );
  const outputReductionPct = Math.round((1 - outputMultiplier) * 100);
  const hasOutputConstraint = !isStorage && outputReductionPct > 0;
  const outputConstraintLabel = `Grid constraint: -${outputReductionPct}%`;
  const outputConstraintDetail =
    `${outputReductionPct}% output reduction due to Grid Connection Constraint.`;
  const firmAdjusted =
    !isStorage &&
    dynamicFirm &&
    Math.abs(dynamicFirm.effective - dynamicFirm.base) > 1e-9;
  const firmIncreased =
    firmAdjusted && dynamicFirm.effective > dynamicFirm.base;
  const firmReduced =
    firmAdjusted && dynamicFirm.effective < dynamicFirm.base;
  const firmReducesFleet = dynamicFirm?.fleetGain < -1e-9;
  const hasFirmBreakdown =
    isVariable &&
    Boolean(dynamicFirm) &&
    (firmAdjusted || Math.abs(dynamicFirm.fleetGain) > 1e-9);
  const firmLabel = showFirmCapacity
    ? formatGw(
        dynamicFirm && !isStorage ? dynamicFirm.effective : baseFirmValue(plant)
      )
    : null;
  const effectiveCapital = dynamicCapital?.effectiveCapital ?? plant.capital;
  const resourcePremium = dynamicCapital?.resourceDevelopmentPremium ?? 0;

  const typeBadge = isStorage
    ? 'Storage'
    : isVariable
      ? 'Variable'
      : 'Reliable';
  // Storage only ever answers the peak (section 16.5). The
  // distinction between the two is how long a charge lasts, not
  // which layer it serves.
  const storageCountsTowards = plant.carriesEnergyBetweenYears
    ? 'This year and next'
    : 'This year only';
  const storageAriaDetail = isStorage
    ? `, firm capacity ${firmLabel ?? baseFirmLabel(plant)} when charged from expected surplus, counts towards firm capacity ${storageCountsTowards.toLowerCase()}`
    : '';

  const wrapperProps = selectable
    ? {
        type: 'button',
        onClick: onSelect,
        disabled,
        'data-audio-cue': 'none',
        'aria-label': `Add ${plant.name} to your plan. Capital ${formatMoney(effectiveCapital)}${resourcePremium > 0 ? `, including a ${formatMoney(resourcePremium)} resource development premium` : ''}, opex ${formatMoney(dynamicOpex?.final ?? plant.opex)} per year, output ${outputLabel}${hasOutputConstraint ? `, ${outputConstraintDetail}` : ''}${storageAriaDetail}, emissions ${formatEmissions(plant.emissions)}.`
      }
    : {};

  const effectiveOpex = dynamicOpex?.final ?? plant.opex;
  const isDiscounted = dynamicOpex?.discounted === true;
  const imageSrc = plant.imageSrc ?? `/plant-images/${plant.id}.webp`;

  const tileContent = (
    <>
      <header className="plant-tile-header">
        <span className="plant-tile-family">
          {FAMILY_LABEL[plant.family] ?? plant.family}
        </span>
        {!isStorage ? (
          <span className={`plant-tile-type plant-tile-type-${plant.type}`}>
            {typeBadge}
          </span>
        ) : (
          <span
            className="plant-tile-type plant-tile-type-storage plant-tile-storage-type-placeholder"
            aria-hidden="true"
          >
            Storage
          </span>
        )}
      </header>
      <span className="plant-tile-image-wrap">
        <img
          className={
            'plant-tile-image' +
            (constructing ? ' plant-tile-image-building-base' : '')
          }
          src={imageSrc}
          alt=""
          loading="eager"
          decoding="async"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        {constructing ? (
          <img
            className="plant-tile-build-image-reveal"
            src={imageSrc}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            style={{
              '--build-reveal-duration': `${constructionDurationMs ?? 300}ms`
            }}
          />
        ) : null}
      </span>
      <h3 className="plant-tile-name">{plant.name}</h3>
      <dl className="plant-tile-stats">
        <div>
          <dt>Capital</dt>
          <dd>
            <span
              className="plant-tile-opex-wrapper"
              tabIndex={resourcePremium > 0 ? 0 : undefined}
            >
              {resourcePremium > 0 ? (
                <span className="plant-tile-opex-base-strike">
                  {formatMoney(plant.capital)}
                </span>
              ) : null}
              <span
                className={
                  'plant-tile-opex-value' +
                  (resourcePremium > 0 ? ' plant-tile-capital-premium' : '')
                }
              >
                {formatMoney(effectiveCapital)}
              </span>
              {resourcePremium > 0 ? (
                <span
                  className="plant-tile-opex-tooltip plant-tile-capital-tooltip"
                  role="tooltip"
                >
                  {buildCapitalReasonLines(
                    plant,
                    resourcePremium,
                    effectiveCapital
                  ).map((line, i) => (
                    <span key={i} className="plant-tile-opex-tooltip-line">
                      {line}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          </dd>
        </div>
        <div>
          <dt>Opex / year</dt>
          <dd>
            <span className="plant-tile-opex-wrapper">
              {isDiscounted ? (
                <span className="plant-tile-opex-base-strike">
                  {formatMoney(dynamicOpex.base)}
                </span>
              ) : null}
              <span
                className={
                  'plant-tile-opex-value' +
                  (isDiscounted ? ' plant-tile-opex-discounted' : '')
                }
              >
                {formatMoney(effectiveOpex)}
              </span>
              {isDiscounted ? (
                <span className="plant-tile-opex-tooltip" role="tooltip">
                  {buildOpexReasonLines(dynamicOpex).map((line, i) => (
                    <span key={i} className="plant-tile-opex-tooltip-line">
                      {line}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          </dd>
        </div>
        {isStorage ? (
          <>
            <div>
              <dt>Stores</dt>
              <dd>{formatTwh(plant.storageEnergyUnits)}</dd>
            </div>
            <div>
              <dt>Firm capacity</dt>
              <dd>{baseFirmLabel(plant)}</dd>
            </div>
            <div className="plant-tile-storage-counts">
              <dt>Counts towards</dt>
              <dd>{storageCountsTowards}</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Output</dt>
              <dd>
                <span className="plant-tile-output-wrapper">
                  <span>{outputLabel}</span>
                  {hasOutputConstraint ? (
                    <span className="plant-tile-output-note">
                      {outputConstraintLabel}
                    </span>
                  ) : null}
                </span>
              </dd>
            </div>
            {firmLabel ? (
              <div>
                <dt>Firm capacity</dt>
                <dd>
                  {/* Same wrapper as opex: it carries the positioning
                      and the :hover / :focus-within trigger. */}
                  <span
                    className="plant-tile-opex-wrapper"
                    tabIndex={hasFirmBreakdown ? 0 : undefined}
                    aria-label={
                      hasFirmBreakdown
                        ? `Firm capacity breakdown for ${plant.name}`
                        : undefined
                    }
                  >
                    {firmAdjusted ? (
                      <span className="plant-tile-opex-base-strike">
                        {formatGw(dynamicFirm.base)}
                      </span>
                    ) : null}
                    <span
                      className={
                        'plant-tile-opex-value' +
                        (firmIncreased ? ' plant-tile-opex-discounted' : '') +
                        (firmReduced || firmReducesFleet
                          ? ' plant-tile-firm-reduced'
                          : '')
                      }
                      data-testid={`plant-firm-${plant.id}`}
                    >
                      {firmLabel}
                    </span>
                    {hasFirmBreakdown ? (
                      <span className="plant-tile-opex-tooltip" role="tooltip">
                        {buildFirmReasonLines(dynamicFirm).map((line, i) => (
                          <span key={i} className="plant-tile-opex-tooltip-line">
                            {line}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </span>
                </dd>
              </div>
            ) : null}
            <div>
              <dt>Emissions</dt>
              <dd
                className={
                  plant.emissions > 0
                    ? 'plant-tile-emit-pos'
                    : plant.emissions < 0
                      ? 'plant-tile-emit-neg'
                      : ''
                }
              >
                {formatEmissions(plant.emissions)}
              </dd>
            </div>
          </>
        )}
      </dl>
    </>
  );

  const removeButton = removable ? (
    <button
      type="button"
      className="plant-tile-remove"
      onClick={onRemove}
      aria-label={`Remove ${plant.name}`}
    >
      Remove
    </button>
  ) : null;

  const className =
    `plant-tile plant-tile-${plant.family}` +
    (selectable ? ' plant-tile-selectable' : '') +
    (disabled ? ' plant-tile-disabled' : '') +
    (constructing ? ' plant-tile-constructing' : '');

  if (isStorage) {
    return (
      <article
        className={className}
        data-testid={selectable ? undefined : testId}
        aria-busy={constructing ? 'true' : undefined}
      >
        {selectable ? (
          <button
            className="plant-tile-storage-select"
            data-testid={testId}
            aria-busy={constructing ? 'true' : undefined}
            {...wrapperProps}
          >
            {tileContent}
          </button>
        ) : (
          tileContent
        )}
        <details className="plant-tile-storage-details">
          <summary
            className="plant-tile-type plant-tile-type-storage"
            aria-label={`${plant.name} storage details`}
          >
            Storage <span aria-hidden="true">▾</span>
          </summary>
          <div className="plant-tile-storage-details-content">
            <dl className="plant-tile-storage-detail-stats">
              <div>
                <dt>Output</dt>
                <dd>None</dd>
              </div>
              <div>
                <dt>Emissions</dt>
                <dd>{formatEmissions(plant.emissions)}</dd>
              </div>
              <div>
                <dt>Surplus to fill</dt>
                <dd>{formatTwh(storageSurplusToFill(plant))}</dd>
              </div>
            </dl>
            <p className="plant-tile-footnote">
              Needs expected surplus to charge
              {plant.carriesEnergyBetweenYears
                ? `. Keeps ${formatPercent(plant.roundTripEfficiency)} of its charge into next year`
                : '. Starts every year empty'}
            </p>
          </div>
        </details>
        {removeButton}
      </article>
    );
  }

  const Wrapper = selectable ? 'button' : 'article';
  return (
    <Wrapper
      className={className}
      data-testid={testId}
      aria-busy={constructing ? 'true' : undefined}
      {...wrapperProps}
    >
      {tileContent}
      {removeButton}
    </Wrapper>
  );
}
